import { ICON_SIZE } from '@components/v2/constants';
import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import List from '@components/v2/ui/List/List';
import Loading from '@components/v2/ui/Loading/Loading';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Divider, Stack } from '@mui/material';
import { ArrowLeft, Info } from 'lucide-react';
import { SyntheticEvent } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { isScrolledToEnd } from '../../../../utils/generics';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import { CriteriaOperatorEnum } from '../../enums/criteriaOperator.enum';
import { CATALOG_CREATION_CRITERIA_OPTIONS_MAX_HEIGHT } from '../catalogCreationCriteria.consts';
import {
  CatalogCreationCriterionOption,
  CatalogCreationCriterionOptionLabelsById,
} from '../catalogCreationCriteria.types';
import { mergeSelectedCatalogCreationCriterionOptions } from '../catalogCreationCriteria.utils';
import CatalogCreationCriteriaOptionRow from './CatalogCreationCriteriaOptionRow';

const SCROLL_END_THRESHOLD_PX = 24;

interface CatalogCreationCriteriaValuePickerProps {
  title: LangEnum;
  search: string;
  isLoading: boolean;
  options: CatalogCreationCriterionOption[];
  selectedIds: string[];
  optionLabelsById: CatalogCreationCriterionOptionLabelsById;
  operator: CriteriaOperatorEnum | null;
  operatorHint?: LangEnum;
  onBack: () => void;
  onApply: () => void;
  onSearchChange: (search: string) => void;
  onOperatorChange: (operator: CriteriaOperatorEnum) => void;
  onToggleOption: (option: CatalogCreationCriterionOption) => void;
  onScrollEnd?: () => void;
}

const CatalogCreationCriteriaValuePicker = ({
  title,
  search,
  isLoading,
  options,
  selectedIds,
  optionLabelsById,
  operator,
  operatorHint,
  onBack,
  onApply,
  onSearchChange,
  onOperatorChange,
  onToggleOption,
  onScrollEnd,
}: CatalogCreationCriteriaValuePickerProps) => {
  const { translate } = useTranslation();

  const visibleOptions = mergeSelectedCatalogCreationCriterionOptions({
    options,
    selectedIds,
    optionLabelsById,
  });

  const hasOptions = visibleOptions.length > 0;

  const operatorOptions = [
    { value: CriteriaOperatorEnum.AND, label: translate(LangEnum.GLOBAL_AND) },
    { value: CriteriaOperatorEnum.OR, label: translate(LangEnum.GLOBAL_OR) },
  ];

  const handleScroll = (event: SyntheticEvent) => {
    if (isScrolledToEnd(event.currentTarget as HTMLElement, SCROLL_END_THRESHOLD_PX)) {
      onScrollEnd?.();
    }
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', columnGap: 1, px: 1, pt: 1, flexShrink: 0 }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', columnGap: 1, flexGrow: 1, minWidth: 0 }}
        >
          <IconButton
            icon={ArrowLeft}
            size="small"
            color="primary"
            ariaLabel={translate(LangEnum.GLOBAL_BACK)}
            onClick={onBack}
          />
          <Typography noWrap variant="subtitle2">
            {translate(title)}
          </Typography>
        </Stack>
        {operator && operatorHint && (
          <Tooltip placement="top" tabIndex={-1} TooltipContent={translate(operatorHint)}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Icon icon={Info} size="sm" />
            </Box>
          </Tooltip>
        )}
        {operator && (
          <Select
            size="small"
            fullWidth={false}
            value={operator}
            options={operatorOptions}
            testId={CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_OPERATOR}
            sx={(theme) => ({
              flexShrink: 0,
              '& .MuiOutlinedInput-root': { borderRadius: theme.shape.borderRadiusFull },
              '&&& .MuiSelect-select': {
                paddingLeft: theme.spacing(1.25),
                paddingRight: `calc(${theme.spacing(1.5)} + ${ICON_SIZE.md}px)`,
              },
            })}
            onChange={(event) => onOperatorChange(event.target.value as CriteriaOperatorEnum)}
          />
        )}
      </Stack>

      <Box sx={{ px: 1, pt: 1.5, flexShrink: 0 }}>
        <TextField
          size="small"
          value={search}
          placeholder={translate(LangEnum.GLOBAL_FILTER)}
          slotProps={{
            htmlInput: { 'test-id': CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_SEARCH },
          }}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          pt: 1,
          px: 0.5,
          overflowY: 'auto',
          minHeight: isLoading ? CATALOG_CREATION_CRITERIA_OPTIONS_MAX_HEIGHT : 0,
        }}
        onScroll={handleScroll}
      >
        <List>
          {visibleOptions.map((option) => (
            <CatalogCreationCriteriaOptionRow
              key={option.id}
              label={option.name}
              description={option.description}
              testId={`${CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_OPTION_PREFIX}${option.id}`}
              isChecked={selectedIds.includes(option.id)}
              onClick={() => onToggleOption(option)}
            />
          ))}
        </List>
        {isLoading && (
          <Stack sx={{ alignItems: 'center', py: 1.5 }}>
            <Loading size={ICON_SIZE.lg} />
          </Stack>
        )}
        {!hasOptions && !isLoading && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', px: 1.5, py: 1.5, display: 'block' }}
          >
            {translate(LangEnum.GLOBAL_NO_RESULTS)}
          </Typography>
        )}
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          testId={CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_APPLY}
          onClick={onApply}
        >
          {translate(LangEnum.GLOBAL_APPLY)}
        </Button>
      </Box>
    </Stack>
  );
};

export default CatalogCreationCriteriaValuePicker;
